'use client';

import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, Send, Sparkles, X, Car } from 'lucide-react';

import { detectSiteLang, useLocale, type SiteLang } from '@/lib/i18n';

interface ChatRec {
  id: string;
  name: string;
  category: string;
  pricePerDay: number;
  seats: number;
  transmission: string;
  fuelType: string;
  location: string;
  imageUrl: string;
}

interface ChatMsg {
  role: 'user' | 'assistant';
  content: string;
  recs?: ChatRec[];
}

interface AssistantWidgetProps {
  onReserve: (
    vehicleId: string,
    prefill: { pickupDate?: string; dropoffDate?: string; location?: string } | null,
  ) => void;
}

type Lang = SiteLang;

const GREETINGS: Record<Lang, string> = {
  fr: 'Salut ! Je suis Sara, de fleetava Agadir. Dis-moi tes dates et ce qu’il te faut — je te trouve la voiture idéale en quelques secondes.',
  en: 'Hi! I’m Sara from fleetava Agadir. Tell me your dates and what you need — I’ll find your ideal car in seconds.',
  ar: 'مرحباً! أنا سارة من fleetava أكادير. أخبرني بتواريخك وما تحتاجه — وسأجد لك السيارة المثالية في ثوانٍ.',
  es: '¡Hola! Soy Sara, de fleetava Agadir. Dime tus fechas y lo que necesitas — te encuentro el coche ideal en segundos.',
};

const QUICK: Record<Lang, string[]> = {
  fr: ['Je cherche une voiture pas chère', 'Il nous faut 7 places', 'Un SUV pour le désert', 'Devis pour une semaine'],
  en: ['I need a cheap car', 'We need 7 seats', 'An SUV for the desert', 'Quote for one week'],
  ar: ['أبحث عن سيارة رخيصة', 'نحتاج 7 مقاعد', 'سيارة دفع رباعي للصحراء', 'عرض سعر لأسبوع'],
  es: ['Busco un coche barato', 'Necesitamos 7 plazas', 'Un SUV para el desierto', 'Presupuesto por una semana'],
};

const CHROME: Record<Lang, { online: string; typing: string; offline: string; reserve: string; placeholder: string; paused: string; retry: string }> = {
  fr: { online: 'En ligne — répond vite', typing: 'Écrit…', offline: 'Hors ligne', reserve: 'Réserver', placeholder: 'Dates, budget, places…', paused: 'Je suis en pause pour le moment — appelle-nous directement et on s’occupe de toi !', retry: 'Petit souci de connexion — réessaie dans un instant.' },
  en: { online: 'Online — replies fast', typing: 'Typing…', offline: 'Offline', reserve: 'Reserve', placeholder: 'Dates, budget, seats…', paused: 'I’m paused right now — call us and we’ll take care of you.', retry: 'Connection trouble — try again in a moment.' },
  ar: { online: 'متصلة — ترد بسرعة', typing: 'تكتب…', offline: 'غير متصلة', reserve: 'احجز', placeholder: 'التواريخ، الميزانية، المقاعد…', paused: 'أنا متوقفة الآن — اتصل بنا وسنتولى الأمر.', retry: 'مشكلة في الاتصال — أعد المحاولة بعد لحظة.' },
  es: { online: 'En línea — responde rápido', typing: 'Escribiendo…', offline: 'Fuera de línea', reserve: 'Reservar', placeholder: 'Fechas, presupuesto, plazas…', paused: 'Estoy en pausa ahora — llámanos y te atendemos.', retry: 'Problema de conexión — inténtalo en un momento.' },
};

function isGreeting(content: string): boolean {
  return Object.values(GREETINGS).includes(content);
}

export function AssistantWidget({ onReserve }: AssistantWidgetProps) {
  const [open, setOpen] = useState(false);
  // Single source of truth: the SITE locale. Chat greets in the client's
  // chosen language and the picker below switches the whole website too.
  const { lang, setLang: setSiteLang } = useLocale();
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [lastPrefill, setLastPrefill] = useState<{
    pickupDate?: string;
    dropoffDate?: string;
    location?: string;
  } | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [msgs, open, loading]);

  function greet(code: Lang) {
    setMsgs([{ role: 'assistant', content: GREETINGS[code] }]);
  }

  function handleOpen() {
    setOpen(true);
    if (msgs.length === 0) greet(lang);
  }

  useEffect(() => {
    if (!open) return;
    setMsgs((current) => {
      const greetingOnly = current.length === 1 && current[0]?.role === 'assistant' && isGreeting(current[0].content);
      if (!greetingOnly || current[0]?.content === GREETINGS[lang]) return current;
      return [{ role: 'assistant', content: GREETINGS[lang] }];
    });
  }, [lang, open]);

  async function send(text: string) {
    const content = text.trim();
    if (!content || loading) return;
    const spoken = detectSiteLang(content);
    const replyLang = spoken ?? lang;
    if (spoken && spoken !== lang) setSiteLang(spoken);
    setUnavailable(false);
    const history = [...msgs, { role: 'user' as const, content }];
    setMsgs(history);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch('/api/assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: history.slice(-12).map((m) => ({ role: m.role, content: m.content })),
          lang: replyLang,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 503) {
        setUnavailable(true);
        setMsgs((cur) => [
          ...cur,
          { role: 'assistant', content: CHROME[replyLang].paused },
        ]);
        return;
      }
      if (!res.ok) throw new Error(data.error ?? 'Erreur');
      const recs = Array.isArray(data.recommendations) ? (data.recommendations as ChatRec[]) : [];
      setLastPrefill((data.prefill as typeof lastPrefill) ?? null);
      setMsgs((cur) => [
        ...cur,
        { role: 'assistant', content: String(data.reply ?? '...'), recs: recs.length ? recs : undefined },
      ]);
    } catch {
      setMsgs((cur) => [
        ...cur,
        { role: 'assistant', content: CHROME[replyLang].retry },
      ]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={handleOpen}
          aria-label="Discuter avec Sara"
          className="fixed bottom-[max(1.25rem,env(safe-area-inset-bottom))] right-[max(1.25rem,env(safe-area-inset-right))] z-30 flex h-14 w-14 items-center justify-center rounded-full bg-slate-950 text-[var(--color-primary)] shadow-xl transition-transform hover:scale-105 active:scale-95"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--color-primary)] text-white">
            <Sparkles className="h-2.5 w-2.5" />
          </span>
        </button>
      )}

      {open && (
        <div className="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] top-auto z-30 flex h-[min(70vh,calc(100dvh-5rem))] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl sm:inset-x-auto sm:bottom-5 sm:right-5 sm:h-[560px] sm:w-[380px]">
          <div className="flex items-center gap-2.5 bg-slate-950 px-4 py-3 text-white">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--color-primary)] text-sm font-black">
              S
            </span>
            <div className="min-w-0">
              <p className="text-sm font-extrabold">Sara · fleetava Agadir</p>
              <p className="flex items-center gap-1 text-[11px] text-slate-300">
                <span className={`h-1.5 w-1.5 rounded-full ${unavailable ? 'bg-rose-400' : 'bg-emerald-400'}`} />
                {unavailable ? CHROME[lang].offline : loading ? CHROME[lang].typing : CHROME[lang].online}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Fermer"
              className="ml-auto rounded-lg p-1.5 text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grow space-y-2.5 overflow-y-auto bg-slate-50 p-3">
              {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-slate-950 text-white'
                      : 'border border-slate-200 bg-white text-slate-800'
                  }`}
                >
                  <p dir="auto" className="whitespace-pre-wrap">{m.content}</p>
                  {m.recs && m.recs.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {m.recs.map((r) => (
                        <div key={r.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                          {r.imageUrl ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={r.imageUrl} alt={r.name} className="h-24 w-full object-cover" loading="lazy" />
                          ) : (
                            <div className="flex h-16 items-center justify-center bg-slate-100 text-slate-400">
                              <Car className="h-6 w-6" />
                            </div>
                          )}
                          <div className="p-2">
                            <p className="text-xs font-extrabold text-slate-900">{r.name}</p>
                            <p className="text-[11px] text-slate-500">
                              {r.category} · {r.seats} places · {r.transmission}
                            </p>
                            <div className="mt-1.5 flex items-center justify-between">
                              <span className="text-sm font-black tabular-nums text-[var(--color-primary)]">
                                {r.pricePerDay} DH<span className="text-[10px] font-medium text-slate-400">/j</span>
                              </span>
                              <button
                                type="button"
                                onClick={() => onReserve(r.id, lastPrefill)}
                                className="rounded-xl bg-slate-950 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-[var(--color-primary)]"
                              >
                                {CHROME[lang].reserve}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="flex gap-1 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                  {[0, 1, 2].map((d) => (
                    <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: `${d * 150}ms` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {msgs.length <= 1 && !loading && (
            <div className="flex flex-wrap gap-1.5 border-t border-slate-100 bg-white px-3 pt-2">
              {QUICK[lang].map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => void send(q)}
                  className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-[var(--color-primary)] hover:text-slate-900"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <form
            className="flex items-center gap-2 border-t border-slate-100 bg-white p-2.5"
            onSubmit={(e) => {
              e.preventDefault();
              void send(input);
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={CHROME[lang].placeholder}
              className="grow rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-[var(--color-primary)] disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              aria-label="Envoyer"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary)] text-white transition-all hover:bg-[var(--color-primary-dark)] disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
